import { useState, useCallback, memo, useRef } from 'react';

import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Text from '@zenbusiness/zui/Text';

import { useUserMode } from '#client/hooks';

import ActivePromptInput from '../ActivePromptInput';
import ArticleOverlay from '../ArticleOverlay';
import DynamicRenderer from '../DynamicRenderer';
import ProductOverlay from '../ProductOverlay';
import {
  ChatUIContainer,
  ChatUIFullHeightContainer,
  ChatUIContentArea,
  ChatUICenteredContent,
  ChatUIInputArea
} from './ChatUI.style';
import Toast from '../Toast';
import { CLICKSTREAM_COMMON_PROPS, TOAST_MESSAGES } from '../utils/constants';
import isDashboardProductPageUrl from '../utils/isDashboardProductPageUrl';
import removeFromInstructions from '../utils/removeFromInstructions';

// Constants
const LOADING_MESSAGE = '';

/**
 * ChatUI Component
 *
 * Main chat interface component that renders instructions dynamically,
 * handles loading states, and manages article and product overlay interactions.
 * Features a persistent input area at the bottom for continuous interaction.
 *
 * Features:
 * - Dynamic instruction rendering via mutation-based fetching
 * - Persistent input area always available for user messages
 * - Load more functionality that fetches one instruction at a time
 * - Article overlay for detailed content
 * - Product overlay for product details and purchase flows
 * - Loading and completion states
 * - Unified message handling for all interaction types
 * - Uncontrolled input mode for optimal performance
 *
 * @param {object} props - Component props
 * @param {Array} props.instructions - Instructions array from parent
 * @param {boolean} props.loading - Loading state from parent
 * @param {boolean} props.loadingNext - Loading next instruction state from parent
 * @param {boolean} props.error - Error state from parent
 * @param {Function} props.addUserResponse - Function to add user response
 * @param {Function} props.addVeloResponse - Function to add velo response
 * @param {Function} props.onMessageSend - Message send handler from parent
 * @param {object} props.activePromptInputRef - Ref to ActivePromptInput component
 * @param {string} props.currentMode - Current mode of the chat
 * @returns {import('react').ReactElement} The ChatUI component
 */
const ChatUI = memo(
  ({
    activePromptInputRef,
    addUserResponse,
    currentMode,
    instructions = [],
    loading = false,
    loadingNext = false,
    onMessageSend,
    sendUserFeedback,
    setInstructions
  }) => {
    // State for article and product handling
    const [activeArticle, setActiveArticle] = useState(null);
    const [activeProduct, setActiveProduct] = useState(null);
    const [isIntersecting, setIsIntersecting] = useState(true); // Start at bottom - more accurate assumption
    const toastRef = useRef(null);

    const userMode = useUserMode();
    // Initialize error logging
    const captureException = useCaptureException({
      tags: {
        frontend: 'assistant-ui',
        operationName: 'chatUI'
      }
    });

    // Clickstream tracking
    const trackButtonPressed = useClickstreamCallback('buttonPressed');

    // Callback for DynamicRenderer to notify us of intersection changes
    const handleIntersectionChange = useCallback((intersecting) => {
      setIsIntersecting(intersecting);
    }, []);

    // Simple logic: show arrow when user scrolled up (target not intersecting)
    const hasMoreBelow = !isIntersecting;

    const closeArticle = useCallback(() => {
      setActiveArticle(null);
      trackButtonPressed({
        ...CLICKSTREAM_COMMON_PROPS,
        elementName: 'edu_article_close',
        elementText: 'Back'
      });
    }, [trackButtonPressed]);

    const closeProduct = useCallback(() => {
      setActiveProduct(null);
      trackButtonPressed({
        ...CLICKSTREAM_COMMON_PROPS,
        elementName: 'product_overlay_close',
        elementText: 'Back'
      });
    }, [trackButtonPressed]);

    const handleDisplayToast = useCallback((message) => {
      toastRef.current.show(message);
    }, []);

    /**
     * Unified message handler for all types of user interactions
     * @param {string} message - The message to send to the assistant
     * @param {boolean} skipOptimisticUpdate - Whether to skip adding UserResponse (for button clicks)
     * @param {string} messageType - The type of message (defaults to 'text')
     * @param {string} agentName - Optional agent name to include in the request
     */
    const handleSendMessage = useCallback(
      async (message, skipOptimisticUpdate = false, messageType = 'text', agentName = null) => {
        if (!message?.trim()) {
          return; // Don't send empty messages
        }

        // Add UserResponse immediately for optimistic update (only for text input)
        if (!skipOptimisticUpdate && addUserResponse) {
          addUserResponse(message.trim(), messageType);
        }

        try {
          // Use parent handler if provided
          if (onMessageSend) {
            await onMessageSend(message, messageType, agentName);
          }
        } catch (errors) {
          captureException(errors, {
            tags: {
              errorType: 'message_send_failed',
              messageType: messageType || 'unknown',
              currentMode: currentMode || 'unknown'
            },
            contexts: {
              chatUI: {
                messageContent: message?.substring?.(0, 100) || 'unknown', // Truncate for privacy
                messageType,
                agentName: agentName || null,
                currentMode,
                skipOptimisticUpdate,
                instructionCount: instructions?.length || 0,
                hasActiveArticle: !!activeArticle
              },
              userAction: {
                action: 'sendMessage',
                timestamp: new Date().toISOString()
              }
            }
          });
          // TODO: Add proper error handling/user notification
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [addUserResponse, onMessageSend]
    );

    /**
     * Handle component clicks (buttons, cards, etc.)
     * @param data
     */
    const handleComponentClick = useCallback(
      async ({ type, payload }) => {
        if (type === 'article') {
          setActiveArticle(payload);
          removeFromInstructions(setInstructions, payload.messageUuid, {
            componentType: 'ButtonPrompt',
            'props.messageType': 'skip'
          });
          return;
        }

        // For products, we open the product overlay if the ctaUrl is a dashboard product page url
        // otherwise we open the link in a new tab
        if (type === 'product') {
          if (isDashboardProductPageUrl(payload.ctaUrl)) {
            setActiveProduct(payload);
          } else if (payload.ctaUrl) {
            window.open(payload.ctaUrl, '_blank');
          }
          // No instruction removal for products
          return;
        }

        if (type === 'button_click') {
          const message = payload.message || 'next';
          const { agentName } = payload;

          // Add UserResponse immediately for optimistic update to the specific instruction group
          if (addUserResponse) {
            addUserResponse(message, payload.messageType, payload.instructionId);
          }

          // Send message to backend (skip optimistic update since we already added UserResponse)
          await handleSendMessage(message, true, payload.messageType, agentName);
          return;
        }

        if (type === 'user_feedback') {
          handleDisplayToast(TOAST_MESSAGES.THANK_YOU);
          await sendUserFeedback(payload.messageUuid, payload.feedback);
        }
      },
      [addUserResponse, handleDisplayToast, handleSendMessage, sendUserFeedback, setInstructions]
    );

    const renderLoadingState = useCallback(() => <Text size="sm">{LOADING_MESSAGE}</Text>, []);

    const getPlaceholderText = useCallback(() => {
      if (loadingNext) {
        return 'Thinking...';
      }
      return 'Ask Velo anything...';
    }, [loadingNext]);

    // If anon there is a banner --
    const containerHeight = !userMode.isAnonymous
      ? 'calc(100dvh - 4.125rem)'
      : 'calc(100dvh - 6.5rem)';
    // Show initial loading state
    if (loading) {
      return (
        <ChatUIFullHeightContainer>
          {/* Content Area */}
          <ChatUICenteredContent>{renderLoadingState()}</ChatUICenteredContent>

          {/* Fixed Input Area */}
          <ChatUIInputArea>
            <ActivePromptInput
              ref={activePromptInputRef}
              disabled
              error={false}
              isAnswering={false}
              placeholder="Ask Velo anything..."
              onSendMessage={handleSendMessage}
            />
          </ChatUIInputArea>
        </ChatUIFullHeightContainer>
      );
    }
    // Main chat interface (removed old full-screen error handling)
    return (
      <ChatUIContainer height={containerHeight}>
        {/* Scrollable Content Area */}
        <ChatUIContentArea>
          <DynamicRenderer
            currentMode={currentMode}
            instructions={instructions}
            onComponentClick={handleComponentClick}
            onIntersectionChange={handleIntersectionChange}
          />
          {loadingNext && renderLoadingState()}
        </ChatUIContentArea>

        {/* Fixed Input Area */}
        <ChatUIInputArea>
          <ActivePromptInput
            ref={activePromptInputRef}
            disabled={loadingNext}
            error={false}
            isAnswering={loadingNext}
            placeholder={getPlaceholderText()}
            showMoreIndicator={hasMoreBelow}
            onSendMessage={handleSendMessage}
          />
        </ChatUIInputArea>

        {/* Article Overlay */}
        <AnimatePresence mode="wait">
          {activeArticle && <ArticleOverlay article={activeArticle} onClose={closeArticle} />}
        </AnimatePresence>

        {/* Product Overlay */}
        <AnimatePresence mode="wait">
          {activeProduct && <ProductOverlay product={activeProduct} onClose={closeProduct} />}
        </AnimatePresence>

        <Toast ref={toastRef} />
      </ChatUIContainer>
    );
  }
);

export default ChatUI;

ChatUI.propTypes = {
  activePromptInputRef: PropTypes.object,
  addUserResponse: PropTypes.func,
  currentMode: PropTypes.string,
  instructions: PropTypes.array,
  loading: PropTypes.bool,
  loadingNext: PropTypes.bool,
  onMessageSend: PropTypes.func,
  sendUserFeedback: PropTypes.func,
  setInstructions: PropTypes.func
};
